import {
  ArrowUpFromLine,
  ClipboardList,
  Cpu,
  Hourglass,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  PieChart,
  UserCheck,
  Users,
  type LucideIcon,
} from "lucide-react";

import type { OrgLevel } from "@/hooks/useOrgLevel";

/**
 * Which surfaces Desk has at each level, and what the home is.
 *
 * The rail and the home page both read this, so they cannot disagree about
 * what a level can reach. A company's home *is* its queue, so it has no
 * separate queue entry; an MSP's home is the queue; above that the home is
 * the portfolio and the queue sits beside it.
 */
export type SurfaceKey =
  | "home"
  | "queue"
  | "mywork"
  | "escalations"
  | "overdue"
  | "reports"
  | "automation"
  | "teams"
  | "campaigns";
export type Home = "queue" | "work" | "portfolio";

/**
 * Which band of the rail a surface sits in.
 *
 * The split is between being *in* the queue and being *about* it. Queue, My
 * work, Escalations and Overdue are four cuts of the same pile of tickets and
 * a technician moves between them all day. Reports, Automation and Teams are
 * the queue's own machinery: how it is measured, what feeds it, and who it
 * routes to. Someone asking "is this earning its keep" is not doing queue
 * work, and a flat list made those two kinds of thing look interchangeable.
 *
 * `null` is the home surface, which is nobody's category — it is the landing.
 */
export type SurfaceGroup = "work" | "manage" | null;

export const GROUP_LABEL: Record<Exclude<SurfaceGroup, null>, string> = {
  work: "Work",
  manage: "Manage",
};

/** Render order. A group with nothing in it at this level is not rendered. */
export const GROUP_ORDER: Array<Exclude<SurfaceGroup, null>> = [
  "work",
  "manage",
];

export interface Surface {
  key: SurfaceKey;
  name: string;
  href: string;
  icon: LucideIcon;
  group: SurfaceGroup;
  end?: boolean;
}

const HOME: Record<OrgLevel, Home> = {
  company: "work",
  msp: "queue",
  distributor: "portfolio",
  platform: "portfolio",
};

const HOME_NAME: Record<Home, string> = {
  queue: "Queue",
  work: "Our work",
  portfolio: "Portfolio",
};

const HOME_ICON: Record<Home, LucideIcon> = {
  queue: ListChecks,
  work: ClipboardList,
  portfolio: LayoutDashboard,
};

export function homeFor(level: OrgLevel): Home {
  return HOME[level];
}

export function surfacesFor(level: OrgLevel): Surface[] {
  const home = HOME[level];
  const out: Surface[] = [
    {
      key: "home",
      name: HOME_NAME[home],
      href: "/desk",
      icon: HOME_ICON[home],
      group: null,
      end: true,
    },
  ];
  if (home === "portfolio") {
    out.push({
      key: "queue",
      name: "Queue",
      href: "/desk/queue",
      icon: ListChecks,
      group: "work",
    });
  }
  // A person's own list at every level: a company technician has work on
  // them too. Overdue is the operator's board and stays off the company rail.
  out.push({
    key: "mywork",
    name: "My work",
    href: "/desk/my-work",
    icon: UserCheck,
    group: "work",
  });
  // Escalations are what an organization *below* handed up, so only a level
  // with something below it can receive one. A company has nobody below it and
  // would get a permanently empty page.
  if (level !== "company") {
    out.push({
      key: "escalations",
      name: "Escalations",
      href: "/desk/escalations",
      icon: ArrowUpFromLine,
      group: "work",
    });
  }
  if (level !== "company") {
    out.push({
      key: "overdue",
      name: "Overdue",
      href: "/desk/overdue",
      icon: Hourglass,
      group: "work",
    });
  }
  out.push({
    key: "reports",
    name: "Reports",
    href: "/desk/reports",
    icon: PieChart,
    group: "manage",
  });
  if (level !== "company") {
    out.push({
      key: "automation",
      name: "Automation",
      href: "/desk/automation",
      icon: Cpu,
      group: "manage",
    });
    out.push({
      key: "teams",
      name: "Teams",
      href: "/desk/teams",
      icon: Users,
      group: "manage",
    });
    // A campaign is one decision applied across many clients. A company has
    // one client — itself — so the grouping has nothing to group.
    out.push({
      key: "campaigns",
      name: "Campaigns",
      href: "/desk/campaigns",
      icon: Megaphone,
      group: "manage",
    });
  }
  return out;
}
